// The public page's branding an admin sets on the Overview, like Vanta's
// Trust Center "Customize": banner (style, colours, text colour, title,
// image), primary and secondary colours, font and favicon. Same rules as the
// API (regodit-api trust-center/service/brand_rules.js).

import { DEFAULT_ACCENT } from "../styles/publicTheme";
import { companyLogoUrl } from "../../../apis/company-logo-apis/companyLogoApis";

export const HERO_TITLE_MAX = 80;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const MIN_CONTRAST = 3;
// The page's dark text colour (publicTheme ink).
const INK = "#121A3A";

// Ready-made colours, all readable with white text and as links on white.
export const BRAND_PRESETS = [
  { name: "Indigo", value: DEFAULT_ACCENT },
  { name: "Navy", value: "#1E3A8A" },
  { name: "Teal", value: "#0F766E" },
  { name: "Green", value: "#15803D" },
  { name: "Purple", value: "#6D28D9" },
  { name: "Rose", value: "#BE123C" },
  { name: "Orange", value: "#C2410C" },
  { name: "Slate", value: "#334155" },
];

export const BANNER_STYLES = [
  { value: "gradient", label: "Gradient" },
  { value: "image", label: "Image" },
  { value: "minimal", label: "None" },
];

export const BANNER_TEXT_COLORS = [
  { value: "auto", label: "Automatic" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

// Google Fonts the page can use. `key` is what the API stores.
export const FONTS = [
  { key: "geist", name: "Geist", family: "Geist", google: "Geist" },
  { key: "inter", name: "Inter", family: "Inter", google: "Inter" },
  { key: "dm_sans", name: "DM Sans", family: "DM Sans", google: "DM+Sans" },
  { key: "manrope", name: "Manrope", family: "Manrope", google: "Manrope" },
  {
    key: "ibm_plex_sans",
    name: "IBM Plex Sans",
    family: "IBM Plex Sans",
    google: "IBM+Plex+Sans",
  },
  {
    key: "plus_jakarta_sans",
    name: "Plus Jakarta Sans",
    family: "Plus Jakarta Sans",
    google: "Plus+Jakarta+Sans",
  },
  {
    key: "work_sans",
    name: "Work Sans",
    family: "Work Sans",
    google: "Work+Sans",
  },
  {
    key: "source_serif_4",
    name: "Source Serif 4 (serif)",
    family: "Source Serif 4",
    google: "Source+Serif+4",
    serif: true,
  },
];

export const fontOf = (key) => FONTS.find((f) => f.key === key) || FONTS[0];

// The admin's own font (fontFamily "custom"): a Google Font by name, or font
// files they uploaded (or copied from their website). Same rules as the API.
export const CUSTOM_FONT_KEY = "custom";
// Used as a CSS family name, so no quotes, brackets or semicolons.
export const CUSTOM_FONT_NAME = /^[A-Za-z0-9][A-Za-z0-9 _.-]{0,59}$/;
export const MAX_FONT_FILES = 8;
export const FONT_WEIGHTS = [
  { value: "400", label: "Regular (400)" },
  { value: "500", label: "Medium (500)" },
  { value: "600", label: "Semibold (600)" },
  { value: "700", label: "Bold (700)" },
  { value: "100 900", label: "All weights (variable font)" },
];
export const FONT_STYLES = [
  { value: "normal", label: "Normal" },
  { value: "italic", label: "Italic" },
];
const CSS_FORMAT = {
  woff2: "woff2",
  woff: "woff",
  ttf: "truetype",
  otf: "opentype",
};
const extensionOf = (path) =>
  (/\.(woff2|woff|ttf|otf)$/i.exec(path || "") || [])[1]?.toLowerCase();

// A Google Fonts stylesheet link for any family name.
export const googleFontUrl = (name) =>
  `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;

// Whether Google Fonts has a family of that name (it answers 400 when not).
export const isGoogleFont = async (name) => {
  try {
    const response = await fetch(googleFontUrl(name));
    return response.ok;
  } catch {
    return null;
  }
};

// The font a company's page uses: one of FONTS, or { key: "custom", name,
// family, files: [{ url, weight, style, format }] } (no files: Google Fonts).
export const brandFontOf = (company) => {
  const key = String(company?.fontFamily || "").toLowerCase();
  const name = String(company?.customFontName || "").trim();
  if (key !== CUSTOM_FONT_KEY || !CUSTOM_FONT_NAME.test(name))
    return fontOf(key);
  const files = (company.customFontFiles || [])
    .filter((f) => f && (f.path || f.url))
    .map((f) => ({
      url: f.url || companyLogoUrl(f.path),
      weight: f.weight || "400",
      style: f.style || "normal",
      format: f.format || extensionOf(f.path),
    }));
  return {
    key: CUSTOM_FONT_KEY,
    name,
    family: name,
    google: files.length ? null : name.replace(/ /g, "+"),
    files,
  };
};

// @font-face rules for a font made of files ("" for any other font).
export const fontFaceCss = (font) =>
  (font?.files || [])
    .filter((f) => f.url && !/["\\\n]/.test(f.url))
    .map(
      (f) =>
        `@font-face{font-family:"${font.family}";src:url("${f.url}")${
          CSS_FORMAT[f.format] ? ` format("${CSS_FORMAT[f.format]}")` : ""
        };font-weight:${f.weight};font-style:${f.style};font-display:swap;}`
    )
    .join("\n");

// CSS font-family for a font, with system fallbacks while it loads.
export const fontStack = (font) =>
  font.serif
    ? `"${font.family}", Georgia, "Times New Roman", serif`
    : `"${font.family}", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif`;

// Google Fonts stylesheet for one font, or several (the admin's picker).
// Fonts made of files are left out.
export const fontsUrl = (fonts) => {
  const google = fonts.filter((f) => f.google);
  return google.length
    ? `https://fonts.googleapis.com/css2?${google
        .map((f) => `family=${f.google}:wght@400;500;600;700`)
        .join("&")}&display=swap`
    : null;
};

const luminance = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

export const contrastWithWhite = (hex) => contrast(hex, "#FFFFFF");

export const isHexColor = (value) => HEX_COLOR.test(value || "");

// Why a colour cannot be used, or "". Empty means the default. `onWhite`:
// it carries white text or sits on white (primary, secondary).
export const colorProblem = (value, { onWhite = false } = {}) => {
  if (!value) return "";
  if (!isHexColor(value)) return "Enter a hex code, like #3B4CE0.";
  if (onWhite && contrastWithWhite(value) < MIN_CONTRAST)
    return "Too light for white text and links. Pick a darker shade.";
  return "";
};

export const brandColorProblem = (value) =>
  colorProblem(value, { onWhite: true });

const usable = (value, onWhite) =>
  isHexColor(value) && !colorProblem(value, { onWhite })
    ? value.toUpperCase()
    : null;

// The colour the public page uses for buttons and links.
export const accentOf = (company) =>
  usable(company?.brandColor, true) || DEFAULT_ACCENT;

// The banner's title: the company's own, or "<name> Trust Center".
export const heroTitleOf = (company) =>
  (company?.heroTitle || "").trim() ||
  `${company?.name || ""} Trust Center`.trim();

// Text on a banner of these colours: white or the page's ink, whichever has
// more contrast against the colour the text is hardest to read on.
export const readableTone = (colors) => {
  const worst = (text) => Math.min(...colors.map((c) => contrast(c, text)));
  return worst("#FFFFFF") >= worst(INK) ? "light" : "dark";
};

// Everything the public page needs, with defaults filled in. Enum values are
// compared lower-case (the admin's GraphQL and the public REST agree, but a
// stray upper-case value must not break the page).
export const brandOf = (company) => {
  const lower = (v) => String(v || "").toLowerCase();
  const accent = accentOf(company);
  const secondary = usable(company?.secondaryColor, true) || accent;
  const style = ["gradient", "image", "minimal"].includes(
    lower(company?.bannerStyle)
  )
    ? lower(company.bannerStyle)
    : company?.heroImagePath
      ? "image"
      : "gradient";
  // An image banner without an image falls back to the gradient.
  const bannerStyle =
    style === "image" && !company?.heroImagePath ? "gradient" : style;
  const start = usable(company?.bannerStartColor, false);
  const end = usable(company?.bannerEndColor, false);
  const chosenText = lower(company?.bannerTextColor);
  // Gradient: the admin's two colours, or deep shades of the primary (set
  // by the banner itself; white text reads on those).
  const gradient = start || end ? [start || end, end || start] : null;
  const autoTone =
    bannerStyle === "minimal"
      ? "dark"
      : bannerStyle === "gradient" && gradient
        ? readableTone(gradient)
        : "light";
  return {
    accent,
    secondary,
    font: brandFontOf(company),
    bannerStyle,
    gradient,
    // A minimal banner has no background, so its text is always dark.
    textTone:
      bannerStyle !== "minimal" &&
      (chosenText === "light" || chosenText === "dark")
        ? chosenText
        : autoTone,
    faviconPath: company?.faviconPath || null,
  };
};
